/**
 * The room's chat, in a corner of the room: what people said, newest at the bottom, and a line to
 * say something. Folds away to a button that counts what arrived while it was closed.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ChevronDown, ImagePlus, MessageSquare, Send, X } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { formatTime } from '../lib/format'
import { chatErrorKey, type RoomChat as Chat } from '../bughunt/useRoomChat'
import { prepareChatPicture, type ChatPicture } from '../bughunt/chatPicture'

const OPEN_KEY = 'devpulse:room-chat-open'

function readOpen(): boolean {
  try {
    const saved = localStorage.getItem(OPEN_KEY)
    if (saved !== null) return saved === '1'
  } catch {
    // Storage blocked: fall through to the default.
  }
  return window.matchMedia?.('(min-width: 1100px)').matches ?? false
}

export function RoomChat({ chat, meDeveloperId }: { chat: Chat; meDeveloperId: string | null | undefined }) {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState(readOpen)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [picture, setPicture] = useState<ChatPicture | null>(null)
  const [seen, setSeen] = useState(chat.messages.length)
  const list = useRef<HTMLOListElement>(null)

  const toggle = (next: boolean): void => {
    setOpen(next)
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0')
    } catch {
      // Remembering is a nicety.
    }
  }

  const count = chat.messages.length
  const last = chat.messages.at(-1)?.id

  // Open: everything is seen, and the newest line is in view unless the reader scrolled up.
  useEffect(() => {
    if (!open) return
    setSeen(count)
    const element = list.current
    if (element && element.scrollHeight - element.scrollTop - element.clientHeight < 80) element.scrollTop = element.scrollHeight
  }, [open, count, last])

  // First open: jump to the bottom.
  useEffect(() => {
    if (open && list.current) list.current.scrollTop = list.current.scrollHeight
  }, [open])

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    const line = text.trim()
    if ((!line && !picture) || chat.sending) return
    setError(null)
    try {
      await chat.say(line, picture?.dataUrl)
      setText('')
      setPicture(null)
    } catch (failure) {
      setError(t(chatErrorKey(failure)))
    }
  }

  /** A picture is shrunk here before it is sent, and refused plainly when it cannot be. */
  const attach = async (file: File | undefined): Promise<void> => {
    if (!file) return
    setError(null)
    const prepared = await prepareChatPicture(file)
    if (typeof prepared === 'string') {
      setError(t(`room.chat.picture.${prepared}`))
      return
    }
    setPicture(prepared)
  }

  const unread = Math.max(0, count - seen)

  if (!open) {
    return (
      <button type="button" className="room-chat__fab" onClick={() => toggle(true)} aria-label={t('room.chat.open')}>
        <MessageSquare size={15} aria-hidden /> {t('room.chat.title')}
        {unread > 0 && <span className="room-chat__badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
    )
  }

  return (
    <section className="room-chat" aria-label={t('room.chat.title')}>
      <header className="room-chat__head">
        <MessageSquare size={14} aria-hidden />
        <span className="small bold">{t('room.chat.title')}</span>
        <div className="spacer" />
        <button type="button" className="room-chat__fold" onClick={() => toggle(false)} aria-label={t('room.chat.close')} title={t('room.chat.close')}>
          <ChevronDown size={15} />
        </button>
      </header>

      <ol className="room-chat__list" ref={list} aria-live="polite">
        {chat.messages.length === 0 && <li className="room-chat__empty tiny">{t('room.chat.empty')}</li>}
        {chat.messages.map((message) => (
          <li key={message.id} className={`room-chat__line${message.developerId && message.developerId === meDeveloperId ? ' room-chat__line--me' : ''}${message.byAgent ? ' room-chat__line--agent' : ''}`}>
            <span className="room-chat__meta">
              <span className="room-chat__who">{message.authorName}</span>
              {/* Said by an agent, not by the person who owns it. */}
              {message.byAgent && <span className="room-chat__agent">AI</span>}
              <time dateTime={message.createdAt}>{formatTime(message.createdAt, locale)}</time>
            </span>
            {message.text && <span className="room-chat__text">{message.text}</span>}
            {/* A picture lasts a minute, and says how long it has left. */}
            {message.image && <ChatPictureView src={message.image} seconds={message.imageSeconds ?? 0} />}
          </li>
        ))}
      </ol>

      {picture && (
        <div className="room-chat__attached">
          <img src={picture.dataUrl} alt="" width={picture.width} height={picture.height} />
          <span className="tiny muted">{t('room.chat.picture.lasts')}</span>
          <button type="button" className="btn btn--sm btn--ghost btn--icon" onClick={() => setPicture(null)} aria-label={t('common.cancel')}>
            <X size={13} />
          </button>
        </div>
      )}

      <form className="room-chat__form" onSubmit={(event) => void submit(event)}>
        <label className="btn btn--sm btn--ghost btn--icon room-chat__attach" title={t('room.chat.picture.add')}>
          <ImagePlus size={14} aria-hidden />
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(event) => {
              void attach(event.target.files?.[0])
              event.target.value = ''
            }}
            aria-label={t('room.chat.picture.add')}
          />
        </label>
        <input
          className="input"
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={280}
          placeholder={t('room.chat.placeholder')}
          aria-label={t('room.chat.placeholder')}
        />
        <button
          type="submit"
          className="btn btn--sm btn--primary btn--icon"
          disabled={(!text.trim() && !picture) || chat.sending}
          aria-label={t('room.say.send')}
        >
          <Send size={13} />
        </button>
      </form>
      {error && <span className="room-chat__error tiny">{error}</span>}
    </section>
  )
}

/**
 * A picture in the chat, with what is left of its minute.
 *
 * It goes when the time is up, here and on the server: the countdown is the promise, so it is
 * drawn rather than implied. The line stays behind, saying a picture was there.
 */
function ChatPictureView({ src, seconds }: { src: string; seconds: number }) {
  const { t } = useI18n()
  const [left, setLeft] = useState(seconds)

  useEffect(() => {
    setLeft(seconds)
    if (seconds <= 0) return
    const timer = window.setInterval(() => setLeft((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [seconds, src])

  if (left <= 0) return <span className="room-chat__gone tiny">{t('room.chat.picture.gone')}</span>

  return (
    <span className="room-chat__picture">
      <img src={src} alt={t('room.chat.picture.alt')} />
      <span className="room-chat__countdown tiny" aria-label={t('room.chat.picture.left', { count: left })}>{left}s</span>
    </span>
  )
}
